import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Cpu, Camera, X, Loader2, RefreshCw, FileDown, Paperclip } from "lucide-react";
import CognitiveModeSelector from "@/components/CognitiveModeSelector";
import { api, streamChat } from "@/lib/api";
import { COGNITIVE_MODES } from "@/constants/modes";
import { useCamera } from "@/hooks/useCamera";
import { toast } from "sonner";

function CameraCaptureModal({ onClose, onCapture, capturing }) {
  const { videoRef, active, facing, start, capture, flip } = useCamera();

  useEffect(() => { start(); }, [start]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-panel rounded-2xl p-5 w-full max-w-md"
        data-testid="chat-camera-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-white text-sm">Show AVA Something</h3>
          <button data-testid="close-chat-camera-modal" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="relative aspect-video bg-black/60 rounded-xl border border-white/10 overflow-hidden mb-3">
          <video ref={videoRef} className={`w-full h-full object-cover ${active ? "" : "hidden"}`} muted playsInline />
          {active && (
            <button
              data-testid="chat-camera-flip"
              onClick={flip}
              title={`Switch to ${facing === "environment" ? "front" : "rear"} camera`}
              className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center bg-black/70 hover:bg-black/85 border border-white/15 text-slate-200 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={() => capturing || onCapture(capture())}
          disabled={capturing || !active}
          data-testid="chat-camera-capture-button"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {capturing ? "AVA is looking..." : "Capture & Send"}
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function BrainConsole({ conversation, onModeChange, onExchangeComplete, allowVision = true }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [liveText, setLiveText] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [visionBusy, setVisionBusy] = useState(false);
  const [flashPdfId, setFlashPdfId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const scrollRef = useRef(null);
  const flashTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  const CHUNK_SIZE = 2 * 1024 * 1024;

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  // Small files: one multipart POST (browser sets its own multipart boundary - never
  // set Content-Type manually, that breaks Safari/iOS uploads). Large files (iPhone
  // scan PDFs etc.): base64-chunked protocol so proxy body-size limits never bite.
  const uploadFileSmart = async (f, onProgress) => {
    if (f.size <= 6 * 1024 * 1024) {
      const form = new FormData();
      form.append("file", f);
      const res = await api.post("/files/upload", form);
      return res.data;
    }
    const begin = await api.post("/files/upload/begin", {
      filename: f.name,
      content_type: f.type || "application/octet-stream",
      total_size: f.size,
    });
    const uploadId = begin.data.upload_id;
    const total = Math.ceil(f.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const b64 = await blobToBase64(f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      await api.post("/files/upload/chunk", { upload_id: uploadId, index: i, data_b64: b64 });
      onProgress && onProgress(Math.round(((i + 1) / total) * 100));
    }
    const fin = await api.post("/files/upload/finish", { upload_id: uploadId });
    return fin.data;
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    e.target.value = "";
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} is over the 25MB limit.`);
        continue;
      }
      const tempId = `tmp-${Date.now()}-${f.name}`;
      setAttachments((prev) => [...prev, { id: tempId, name: f.name, size: f.size, status: "uploading", progress: 0 }]);
      try {
        const data = await uploadFileSmart(f, (pct) => {
          setAttachments((prev) => prev.map((a) => (a.id === tempId ? { ...a, progress: pct } : a)));
        });
        setAttachments((prev) => prev.map((a) => (a.id === tempId ? { id: data.file_id, name: a.name, size: a.size, status: "ready" } : a)));
        toast.success(`${f.name} attached${data.extracted_chars ? "" : " (no readable text found - AVA will say so)"}.`);
      } catch (err) {
        setAttachments((prev) => prev.filter((a) => a.id !== tempId));
        toast.error(`${f.name} upload failed - ${err?.response?.data?.detail || "try again"}.`);
      }
    }
  };

  useEffect(() => {
    if (!conversation) return;
    api.get(`/conversations/${conversation.id}/messages`).then((res) => setMessages(res.data));
  }, [conversation]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, liveText]);

  const modeCfg = COGNITIVE_MODES.find((m) => m.id === conversation?.mode) || COGNITIVE_MODES[0];

  const handleSend = async () => {
    const ready = attachments.filter((a) => a.status === "ready");
    if ((!input.trim() && ready.length === 0) || streaming || !conversation) return;
    if (attachments.some((a) => a.status === "uploading")) {
      toast.info("Hold on - a file is still uploading.");
      return;
    }
    const userText = input.trim() || "Analyze the attached file(s), Doc here.";
    setInput("");
    setMessages((prev) => [...prev, {
      role: "user",
      content: userText,
      attachments: ready.map((a) => ({ file_id: a.id, filename: a.name, size: a.size })),
      created_at: new Date().toISOString(),
    }]);
    setAttachments([]);
    setStreaming(true);
    setLiveText("");

    let acc = "";
    await streamChat(
      conversation.id,
      userText,
      (token) => { acc += token; setLiveText(acc); },
      (sources, messageId) => {
        setMessages((prev) => [...prev, { id: messageId, role: "assistant", content: acc, sources, created_at: new Date().toISOString() }]);
        setLiveText("");
        setStreaming(false);
        // Report ready - flash the download button so Doc can't miss it.
        if (messageId) {
          setFlashPdfId(messageId);
          clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => setFlashPdfId(null), 10000);
        }
        onExchangeComplete && onExchangeComplete();
      },
      (err) => {
        setMessages((prev) => [...prev, { role: "assistant", content: `[SIGNAL LOST] ${err}`, created_at: new Date().toISOString() }]);
        setLiveText("");
        setStreaming(false);
      },
      ready.map((a) => a.id)
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDownloadPdf = async (messageId, i) => {
    if (!messageId || !conversation) {
      toast.error("This reply hasn't been saved yet - send another message first.");
      return;
    }
    try {
      const res = await api.get(
        `/reports/conversation/${conversation.id}/message/${messageId}.pdf`,
        { responseType: "blob" },
      );
      // Turn the streamed blob into a downloadable file - filename comes from the
      // Content-Disposition header when present, else a sensible default.
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `ava-report-${(conversation.title || "session").slice(0, 40)}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded - ${filename}`);
    } catch (e) {
      toast.error("Report download failed - try once more.");
    }
  };

  const handleDownloadConversationPdf = async () => {
    if (!conversation) return;
    try {
      const res = await api.get(`/reports/conversation/${conversation.id}.pdf`, { responseType: "blob" });
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `ava-conversation-${(conversation.title || "session").slice(0, 40)}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded - ${filename}`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No messages in this conversation yet." : "Conversation report failed - try once more.");
    }
  };

  const handleCameraCapture = async (image) => {
    if (!image || !conversation) return;
    setVisionBusy(true);
    try {
      const res = await api.post("/vision/chat-describe", { conversation_id: conversation.id, image });
      setMessages((prev) => [
        ...prev,
        { role: "user", type: "image", content: image, created_at: new Date().toISOString() },
        { role: "assistant", type: "text", content: res.data.description, sources: res.data.sources, created_at: new Date().toISOString() },
      ]);
      setShowCamera(false);
      onExchangeComplete && onExchangeComplete();
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: "[VISION SIGNAL LOST] Could not process that frame.", created_at: new Date().toISOString() }]);
      setShowCamera(false);
    } finally {
      setVisionBusy(false);
    }
  };

  if (!conversation) {
    return (
      <div className="col-span-1 md:col-span-7 h-full flex items-center justify-center glass-panel rounded-2xl">
        <p className="text-slate-500 text-sm font-mono-data" data-testid="no-session-placeholder">Start a new session to engage AVA.</p>
      </div>
    );
  }

  return (
    <div className="col-span-1 md:col-span-7 h-full flex flex-col glass-panel rounded-2xl overflow-hidden min-h-0" data-testid="brain-console">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 2, repeat: Infinity }}>
            <Cpu className="w-4 h-4" style={{ color: modeCfg.color }} />
          </motion.div>
          <span className="font-display font-semibold text-sm text-white">AVA Brain Console</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadConversationPdf}
            data-testid="download-conversation-pdf"
            title="Download this entire conversation as a branded PDF report"
            className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-cyan-300/90 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/25 hover:border-cyan-300 rounded-lg px-2.5 py-1.5 transition-colors"
          >
            <FileDown className="w-3 h-3" /> PDF
          </button>
          <CognitiveModeSelector mode={conversation.mode} onChange={onModeChange} disabled={streaming} />
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {messages.length === 0 && !liveText && (
          <p className="text-slate-500 text-sm font-mono-data" data-testid="console-empty-state">
            AVA core online. Cognitive mode: {modeCfg.label}. Speak, Doc.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`message-${m.role}-${i}`} className="text-left">
            <p className="text-[10px] font-mono-data uppercase tracking-widest mb-1.5" style={{ color: m.role === "user" ? "#94A3B8" : modeCfg.color }}>
              {m.role === "user" ? "Doc" : "AVA"}
            </p>
            {m.type === "image" ? (
              <img src={m.content} alt="Captured for AVA" className="max-w-[220px] rounded-lg border border-white/10" data-testid={`message-image-${i}`} />
            ) : (
              <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap font-mono-data">{m.content}</p>
            )}
            {m.attachments && m.attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`message-attachments-${i}`}>
                {m.attachments.map((a, ai) => (
                  <span key={ai} className="flex items-center gap-1 text-[9px] font-mono-data text-cyan-300/80 bg-black/30 border border-cyan-400/20 rounded-full px-2 py-0.5">
                    <Paperclip className="w-2.5 h-2.5" /> {a.filename}
                  </span>
                ))}
              </div>
            )}
            {m.sources && m.sources.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`message-sources-${i}`}>
                {m.sources.map((s, si) => (
                  <a
                    key={si}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.description}
                    className="text-[9px] font-mono-data text-slate-500 hover:text-cyan-300 border border-white/10 hover:border-cyan-400/30 rounded px-1.5 py-0.5 transition-colors truncate max-w-[180px]"
                  >
                    [{si + 1}] {s.title}
                  </a>
                ))}
              </div>
            )}
            {m.role === "assistant" && m.id && (
              <button
                onClick={() => { setFlashPdfId(null); handleDownloadPdf(m.id, i); }}
                data-testid={`download-pdf-${i}`}
                className={`mt-3 flex items-center gap-2 text-xs font-mono-data uppercase tracking-wider text-cyan-300 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 hover:border-cyan-300 rounded-lg px-3 py-2 transition-colors shadow-[0_0_18px_rgba(0,240,255,0.15)] ${flashPdfId === m.id ? "pdf-flash" : ""}`}
                title="Download this analysis as a full PDF report with sources"
              >
                <FileDown className="w-3.5 h-3.5" />
                {flashPdfId === m.id ? "Report ready - download PDF" : "Download PDF report"}
              </button>
            )}
          </div>
        ))}
        {liveText && (
          <div data-testid="message-assistant-streaming" className="text-left">
            <p className="text-[10px] font-mono-data uppercase tracking-widest mb-1.5" style={{ color: modeCfg.color }}>AVA</p>
            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap font-mono-data">
              {liveText}<span className="inline-block w-1.5 h-3.5 bg-cyan-400 ml-0.5 animate-pulse" />
            </p>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-white/5">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 pb-2" data-testid="attachment-chips">
            {attachments.map((a) => (
              <span key={a.id} className="flex items-center gap-1.5 text-[10px] font-mono-data text-cyan-300 bg-cyan-400/10 border border-cyan-400/25 rounded-full px-2.5 py-1">
                {a.status === "uploading" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                {a.name.length > 26 ? a.name.slice(0, 23) + "..." : a.name}
                {a.status === "uploading" && a.progress > 0 && <span className="text-cyan-500">{a.progress}%</span>}
                <button
                  onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                  data-testid={`remove-attachment-${a.id}`}
                  className="hover:text-white transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 focus-within:border-cyan-400/40 transition-colors">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Transmit to AVA..."
            rows={1}
            disabled={streaming}
            className="flex-1 bg-transparent resize-none outline-none text-sm text-slate-200 placeholder:text-slate-600 py-1.5 max-h-32 font-mono-data"
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,.txt,text/plain,.csv,text/csv,.md,.json,application/json,image/*"
            className="hidden"
            onChange={handleFilesSelected}
            data-testid="chat-file-input"
          />
          <button
            data-testid="chat-attach-button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={streaming}
            title="Attach files - PDFs, documents, photos (works on iPhone too)"
            className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-cyan-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <button
            data-testid="chat-camera-button"
            onClick={() => setShowCamera(true)}
            disabled={streaming}
            title="Show AVA something via camera"
            className={`p-2 rounded-lg bg-white/5 text-slate-400 hover:text-cyan-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors ${allowVision ? "" : "hidden"}`}
          >
            <Camera className="w-4 h-4" />
          </button>
          <button
            data-testid="send-message-button"
            onClick={handleSend}
            disabled={streaming || (!input.trim() && !attachments.some((a) => a.status === "ready"))}
            className="p-2 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
      {showCamera && (
        <CameraCaptureModal
          onClose={() => setShowCamera(false)}
          onCapture={handleCameraCapture}
          capturing={visionBusy}
        />
      )}
    </div>
  );
}
